import axios from 'axios'

// 創建 API 實例
const userTrackingApi = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'https://api.example.com',
  headers: {
    'Content-Type': 'application/json'
  }
})

/**
 * User Tracking API 服務
 */
class UserTrackingService {
  /**
   * 建立追蹤記錄
   * @param {Object} data - 追蹤資料
   * @param {string} data.thumbmark - 瀏覽器指紋
   * @param {Object} [data.components] - 完整指紋組件
   * @param {string} [data.gameId] - 遊戲 ID
   * @param {string} [data.utmSource] - UTM Source
   * @param {string} [data.utmMedium] - UTM Medium
   * @param {string} [data.utmCampaign] - UTM Campaign
   * @param {string} [data.utmTerm] - UTM Term
   * @param {string} [data.utmContent] - UTM Content
   * @returns {Promise<Object>} API 回應
   */
  async create(data) {
    const response = await userTrackingApi.post('/api/v1/thumbmark-tracking', data)
    return response.data
  }

  /**
   * 更新追蹤記錄
   * @param {Object} data - 追蹤資料
   * @param {string} data.thumbmark - 瀏覽器指紋
   * @param {string} [data.userUuid] - 會員 UUID
   * @param {string} [data.gameId] - 遊戲 ID
   * @param {string} [data.utmSource] - UTM Source
   * @param {string} [data.utmMedium] - UTM Medium
   * @param {string} [data.utmCampaign] - UTM Campaign
   * @param {string} [data.utmTerm] - UTM Term
   * @param {string} [data.utmContent] - UTM Content
   * @returns {Promise<Object>} API 回應
   */
  async update(data) {
    const response = await userTrackingApi.patch('/api/v1/thumbmark-tracking', data)
    return response.data
  }

  /**
   * 追蹤下載事件 (整合 thumbmark 生成、UTM 解析、API 呼叫)
   * @param {Object} options - 選項
   * @param {string} [options.gameId] - 遊戲 ID (優先使用此參數,否則從環境變數取得)
   * @returns {Promise<Object>} API 回應
   */
  async trackDownload(options = {}) {
    const isDebug = process.env.NODE_ENV !== 'production'

    if (isDebug)
      console.debug('[UserTracking] trackDownload 開始執行', { options })


    const thumbmarkService = (await import('@/utils/thumbmark')).default

    // 取得 thumbmark 和 components
    const { thumbmark, components } = await thumbmarkService.getThumbmarkWithComponents()

    if (isDebug) {
      console.debug('[UserTracking] Thumbmark 已取得', {
        thumbmark,
        componentsKeys: Object.keys(components),
        componentsSize: `${(new Blob([JSON.stringify(components)]).size / 1024).toFixed(2)} KB`
      })
    }

    // 解析 UTM 參數
    const searchParams = new URLSearchParams(window.location.search)
    const utmParams = this.parseUtmParams(searchParams)

    if (isDebug)
      console.debug('[UserTracking] UTM 參數已解析', { utmParams })


    // 組合追蹤資料
    const gameId = options.gameId || process.env.VUE_APP_GAME_ID
    const trackingData = this.filterNullParams({
      thumbmark,
      components,
      gameId,
      ...utmParams
    })

    if (isDebug) {
      console.debug('[UserTracking] 追蹤資料已組合', {
        gameId,
        hasComponents: !!trackingData.components,
        trackingDataKeys: Object.keys(trackingData),
        trackingDataSize: `${(new Blob([JSON.stringify(trackingData)]).size / 1024).toFixed(2)} KB`
      })
    }

    // 建立追蹤記錄
    try {
      const result = await this.create(trackingData)

      if (isDebug)
        console.debug('[UserTracking] API 呼叫成功', { result })


      return result
    }
    catch (error) {
      if (isDebug)
        console.error('[UserTracking] API 呼叫失敗', { error })

      throw error
    }
  }

  /**
   * 從遊戲 URL 提取 gameId
   * @param {string} url - 遊戲 URL (例如: https://campaign.365h.city/RTW/index.html)
   * @returns {string|null} gameId (例如: RTW) 或 null
   */
  extractGameIdFromUrl(url) {
    if (!url)
      return null

    try {
      const urlObj = new URL(url)
      const pathSegments = urlObj.pathname.split('/').filter(segment => segment.length > 0)

      // 取得路徑的第一段作為 gameId
      // 例如: /RTW/index.html -> RTW
      return pathSegments[0] || null
    }
    catch (error) {
      console.error('[UserTracking] 無法解析 gameId from URL:', url, error)
      return null
    }
  }

  /**
   * 從 URL 解析 UTM 參數
   * @param {URLSearchParams} searchParams - URL search params
   * @returns {Object} UTM 參數物件
   */
  parseUtmParams(searchParams) {
    return {
      utmSource: searchParams.get('utm_source'),
      utmMedium: searchParams.get('utm_medium'),
      utmCampaign: searchParams.get('utm_campaign'),
      utmTerm: searchParams.get('utm_term'),
      utmContent: searchParams.get('utm_content')
    }
  }

  /**
   * 過濾掉值為 null 的參數
   * @param {Object} params - 參數物件
   * @returns {Object} 過濾後的參數物件
   */
  filterNullParams(params) {
    return Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== null && value !== undefined)
    )
  }
}

// 導出單例
export default new UserTrackingService()